import { FC } from 'react';

import { Text } from '@mantine/core';

import { CollectionFile, ComponentFileType, ComponentFileTypes, digitalConverter, FileVisibility } from 'models';
import { FormatHelpers } from 'helpers/formatters';

import { CollateralPage } from 'components/collateral/CollateralPage';
import { FileTeaser } from 'components/file-teaser/FileTeaser';
import { TeaserCarousel } from 'components/teaser-carousel/TeaserCarousel';
import { useFile } from 'hooks/use-file';
import { DateService } from 'services/DateService';

interface FileGridProps {
    files?: CollectionFile[];
}

const FILE_TYPE_ORDER = {
    [ComponentFileType.DATASHEET]: 1,
    [ComponentFileType.USER_MANUAL]: 2,
    [ComponentFileType.APPLICATION_NOTES]: 3,
    [ComponentFileType.OPERATIONAL_MANUAL]: 4,
    [ComponentFileType.CHARACTERISTICS]: 5,
    [ComponentFileType.DYNAMIC_CHARACTERISTICS]: 6,
    [ComponentFileType.OUTPUT_IMPEDANCE_CHARACTERISTICS]: 7,
    [ComponentFileType.QUICKSTART_GUIDE]: 8,
    [ComponentFileType.CERTIFICATE]: 9,
    [ComponentFileType.OTHER]: 10,
} as Record<ComponentFileType, number>;

const formatFileSize = (size?: number) => {
    if (!size) return '';
    return FormatHelpers.formatValue(size, digitalConverter);
};

const FileCard: FC<{ file: CollectionFile }> = ({ file }) => {
    const { file: fileMeta } = useFile(file.file);

    if (!fileMeta) return null;

    const fileTypeOption = ComponentFileTypes.options.find((option) => option.value === file.type);
    const displayName = fileTypeOption?.label || file.type || fileMeta.filename;

    return (
        <FileTeaser
            href={fileMeta.url}
            download
            file={fileMeta}
            cover={<CollateralPage file={fileMeta} width={240} alt={displayName} />}
            title={displayName}
            meta={
                <>
                    {formatFileSize(fileMeta.filesize)}
                    {fileMeta.createdAt && <> • {DateService.formatDistanceToNow(fileMeta.createdAt)}</>}
                </>
            }
        />
    );
};

export const FileGrid: FC<FileGridProps> = ({ files }) => {
    const visibleFiles = files?.filter((file) => file.visibility !== FileVisibility.PRIVATE);

    if (!visibleFiles || visibleFiles.length === 0) {
        return (
            <Text c="dimmed" py="md">
                No files uploaded
            </Text>
        );
    }

    const sortedFiles = [...visibleFiles].sort((a, b) => {
        const orderA = FILE_TYPE_ORDER[a.type as ComponentFileType] ?? 999;
        const orderB = FILE_TYPE_ORDER[b.type as ComponentFileType] ?? 999;

        return orderA - orderB;
    });

    return (
        <TeaserCarousel>
            {sortedFiles.map((file) => (
                <FileCard key={file.id} file={file} />
            ))}
        </TeaserCarousel>
    );
};
